/**
 * Utility functions for row filtering and status
 */

/**
 * Filter rows based on search term and option status
 * @param {Array} rows - Table rows
 * @param {string} searchTerm - Search term
 * @param {string} optionStatus - Filter status (completo, incompleto, vazio)
 * @returns {Array} - Filtered rows with original indices
 */
export function filterRows(rows, searchTerm, optionStatus) {
  const searchTermLower = (searchTerm || "").toLowerCase();
  const optionStatusLower = (optionStatus || "").toLowerCase();

  return rows
    .map((row, originalIndex) => ({ row, originalIndex }))
    .filter(({ row }) => {
      const matchesSearchTerm = row.some(cell =>
        cell &&
        Array.isArray(cell) &&
        (
          (cell[0]?.toString().toLowerCase().includes(searchTermLower)) ||
          (cell[1]?.toString().toLowerCase().includes(searchTermLower))
        )
      );

      const isRowIncomplete = row.some(cell => {
        if (Array.isArray(cell)) {
          return cell[0] === "" && cell[1] === "";
        }
        return cell === '';
      });

      const isRowComplete = row.every(cell => {
        if (Array.isArray(cell)) {
          return cell[0] !== "" || cell[1] !== "";
        }
        return cell !== '';
      });

      const testRow = row.slice(1);
      const isRowEmpty = testRow.length > 0 && testRow.every(cell => {
        if (Array.isArray(cell)) {
          return cell[0] === "" && cell[1] === "";
        }
        return cell === "";
      });

      let matchesOptionStatus = true;
      if (optionStatusLower === 'completo') {
        matchesOptionStatus = isRowComplete;
      } else if (optionStatusLower === 'incompleto') {
        matchesOptionStatus = isRowIncomplete;
      } else if (optionStatusLower === 'vazio') {
        matchesOptionStatus = isRowEmpty;
      }

      return matchesSearchTerm && matchesOptionStatus;
    });
}

/**
 * Get status icon for a row
 * @param {number} colIndex - Column index
 * @param {boolean} isRowEmpty - Whether row is empty
 * @param {boolean} isRowIncomplete - Whether row is incomplete
 * @param {boolean} isRowComplete - Whether row is complete
 * @returns {JSX.Element|null} - Status icon component
 */
export function getStatusIcon(colIndex, isRowEmpty, isRowIncomplete, isRowComplete) {
  if (colIndex !== 0) {
    return null;
  }

  if (isRowEmpty) {
    return <span className="text-red-500">X</span>;
  } else if (isRowIncomplete) {
    return <span className="text-orange-500">!</span>;
  } else if (isRowComplete) {
    return <span className="text-green-500">✓</span>;
  }

  return null;
}

/**
 * Check row status
 * @param {Array} row - Table row
 * @returns {Object} - Object with status flags
 */
export function getRowStatus(row) {
  const isRowEmpty = row.slice(1).every(cell =>
    Array.isArray(cell) ? cell[0] === "" && cell[1] === "" : cell === ""
  );

  const isRowIncomplete = row.some(cell =>
    Array.isArray(cell) ? cell[0] === "" && cell[1] === "" : cell === ""
  );

  const isRowComplete = row.every(cell =>
    Array.isArray(cell) ? cell[0] !== "" || cell[1] !== "" : cell !== ""
  );

  return {
    isRowEmpty,
    isRowIncomplete,
    isRowComplete
  };
}

/**
 * Create rows with separation lines
 * @param {Array} filteredRows - Filtered rows
 * @param {Object} attributeSeparationLines - Separation line configurations
 * @returns {Array} - Rows with separation lines inserted
 */
export function createRowsWithSeparation(filteredRows, attributeSeparationLines) {
  const rows = [];
  let lastSeparationLineKey = '';

  filteredRows.forEach(({ row, originalIndex }) => {
    const separationLine = attributeSeparationLines?.[originalIndex] ?? ['Outros', 'Especialistas', 'FAO'];

    const normalizedKey = separationLine
      .map(val => (val || '').toLowerCase().trim())
      .join('|');

    if (normalizedKey !== lastSeparationLineKey) {
      const [category] = separationLine;
      if (category?.toLowerCase().trim() !== 'default' && Array.isArray(separationLine) &&
        separationLine.length === 3 && separationLine.every(item => typeof item === 'string')) {
        rows.push({
          type: 'category',
          category: separationLine[0],
          specialists: separationLine[1],
          fao: separationLine[2]
        });
      }
      lastSeparationLineKey = normalizedKey;
    }

    rows.push({ type: 'data', row, originalIndex });
  });

  return rows;
}
